import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Monnify from "monnify-js";
import { Archive, CheckCircle2, Download, Plus } from "lucide-react";

import { archiveInvoice, getInvoices, markInvoiceAsPaid } from "src/api/invoice";
import { initiatePayment, verifyPayment } from "src/api/payments";
import { useDownloadInvoiceMutation } from "src/api/mutations";
import env from "src/config/env";
import { useAuth } from "src/providers/AuthProvider";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import InvoiceDetailModal from "src/components/invoices/InvoiceDetailModal";
import ArchivedInvoicesModal from "src/components/invoices/ArchivedInvoicesModal";
import { Button } from "src/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { DataTable, PageHead, SearchField, StatusBadge, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue, renderCurrency, showInfoToast, showSuccessToast } from "src/lib/utils";
import { invoiceStatusBadge, type InvoiceRow } from "src/types/invoice";

const monnify = new Monnify(env.REACT_APP_MONNIFY_API_KEY, env.REACT_APP_MONNIFY_CONTRACT_CODE);

const formatDate = (value?: string) => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "MMM dd, yyyy");
};

const Invoices = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();

	const invoiceLabel = getCustomizationValue(user, "invoice_label") || "Invoices";
	const invoiceLabelSingular = getCustomizationValue(user, "invoice_label", true) || "Invoice";
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);
	const activeInvoice = searchParams.get("invoice");
	const archivedOpen = searchParams.get("archived") === "1";

	const setParam = (key: string, value: string | null) => {
		const next = new URLSearchParams(searchParams);
		if (value === null) next.delete(key);
		else next.set(key, value);
		setSearchParams(next);
	};

	const { data, isLoading, refetch } = useQuery({
		queryKey: ["invoices", page, limit, search],
		queryFn: () => getInvoices({ page, limit: limit === "All" ? 9999 : limit, search }),
	});

	const { mutate: archiveMutate } = useMutation(archiveInvoice, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || `${invoiceLabelSingular} archived`);
			refetch();
		},
	});

	const { mutate: markPaidMutate } = useMutation(markInvoiceAsPaid, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Marked as paid");
			refetch();
		},
	});

	const { mutate: verifyMutate } = useMutation(verifyPayment, { onSuccess: () => refetch() });

	const { mutate: initiateMutate } = useMutation(initiatePayment, {
		onSuccess: (payload) => {
			monnify.initializePayment({
				...payload,
				onClose: (response: { status?: string; paymentReference?: string }) => {
					if (response.status === "SUCCESS") {
						refetch();
						verifyMutate({ reference: response.paymentReference as string, business_uuid: user?.business_uuid });
					}
				},
			});
		},
	});

	const { mutate: downloadInvoice } = useDownloadInvoiceMutation();

	const list = data?.data;
	const invoices = list?.data ?? [];
	const total = list?.total ?? invoices.length;

	const goToNewInvoice = () => {
		if (!user?.bvn_verified) {
			showInfoToast("Please verify your BVN before proceeding");
			navigate("/account");
			return;
		}
		navigate("/invoices/add");
	};

	const columns: Column<InvoiceRow>[] = [
		{ key: "no", header: `${invoiceLabelSingular} No`, primary: true, cell: (row) => row.invoice_id ?? "—" },
		{
			key: "customer",
			header: `${customerLabel} Name`,
			cell: (row) => [row.first_name, row.last_name].filter(Boolean).join(" ") || "—",
		},
		{ key: "balance", header: "Balance", cell: (row) => renderCurrency(row.balance ?? 0) },
		{
			key: "total",
			header: "Total Amount",
			cell: (row) => <span className="tw-font-semibold">{renderCurrency(row.total_amount ?? 0)}</span>,
		},
		{ key: "due", header: "Due Date", cell: (row) => formatDate(row.due_date) },
		{ key: "status", header: "Status", cell: (row) => <StatusBadge status={invoiceStatusBadge(row.status)} /> },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[210px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-items-center tw-justify-end tw-gap-1" onClick={(event) => event.stopPropagation()}>
					<button
						type="button"
						onClick={() => setParam("invoice", row.uuid)}
						className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
					>
						View
					</button>

					{row.payment_method === "transfer" && row.status !== "paid" && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
								>
									Pay
								</button>
							}
							mutateFn={() =>
								initiateMutate({ customer_id: row.customer_id, invoice_id: row.id, business_uuid: user?.business_uuid })
							}
						/>
					)}

					{row.payment_method === "offline" && row.status !== "paid" && (
						<CustomConfirmDialog
							trigger={
								<button
									type="button"
									className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
								>
									<CheckCircle2 className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
									Mark Paid
								</button>
							}
							mutateFn={() => markPaidMutate({ uuid: row.uuid })}
						/>
					)}

					{row.status === "paid" && (
						<button
							type="button"
							aria-label="Download invoice"
							onClick={() => downloadInvoice(row.uuid)}
							className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
						>
							<Download className="tw-h-3.5 tw-w-3.5" />
						</button>
					)}

					<CustomConfirmDialog
						trigger={
							<button
								type="button"
								aria-label="Archive invoice"
								className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-red hover:tw-bg-red-tint"
							>
								<Archive className="tw-h-3.5 tw-w-3.5" />
							</button>
						}
						mutateFn={() => archiveMutate({ uuid: row.uuid })}
					/>
				</div>
			),
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={invoiceLabel}
				subtitle={isLoading ? "Loading..." : `${total} ${total === 1 ? invoiceLabelSingular.toLowerCase() : invoiceLabel.toLowerCase()}`}
				actions={
					<>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button size="sm" variant="outline">
									More
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onSelect={() => navigate("/invoice-groups")}>Invoice Groups</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem onSelect={() => setParam("archived", "1")}>
									<Archive />
									View Archived
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark" onClick={goToNewInvoice}>
							<Plus className="tw-h-4 tw-w-4" />
							New {invoiceLabelSingular}
						</Button>
					</>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder={`Search ${invoiceLabel.toLowerCase()}...`} />
			</div>

			<DataTable
				data={invoices}
				columns={columns}
				rowKey={(row) => row.uuid}
				loading={isLoading}
				totalRows={total}
				page={page}
				onPageChange={(next) => setParam("page", String(next))}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				onRowClick={(row) => setParam("invoice", row.uuid)}
				emptyTitle={search ? `No ${invoiceLabel.toLowerCase()} match "${search}"` : `No ${invoiceLabel.toLowerCase()} yet`}
				emptyDescription={
					search ? "Try a different search term." : `Raise your first ${invoiceLabelSingular.toLowerCase()} to start collecting payments.`
				}
				emptyAction={
					!search && (
						<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark" onClick={goToNewInvoice}>
							<Plus className="tw-h-4 tw-w-4" />
							New {invoiceLabelSingular}
						</Button>
					)
				}
			/>

			<InvoiceDetailModal uuid={activeInvoice} onOpenChange={(open) => !open && setParam("invoice", null)} />

			<ArchivedInvoicesModal
				open={archivedOpen}
				onOpenChange={(open) => !open && setParam("archived", null)}
				invoiceLabel={invoiceLabel}
			/>
		</div>
	);
};

export default withPermissionWrapper(Invoices, "view_invoices");
